import React, { useEffect, useState } from 'react'

import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'

import {
  AppIcon,
  AppIconNames,
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import { Dialog, DialogTitle } from 'src/components/molecules/dialog'

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

export interface IPdfPreviewDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  pdfUrl: string | null
}

export const PdfPreviewDialog: React.FC<IPdfPreviewDialogProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  pdfUrl,
}) => {
  const { t } = useTranslation()
  const [numPages, setNumPages] = useState<number>(0)
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(
    null,
  )
  const [containerWidth, setContainerWidth] = useState<number>(0)

  const onDocumentLoadSuccess = ({
    numPages: total,
  }: {
    numPages: number
  }): void => {
    setNumPages(total)
  }

  useEffect((): (() => void) | void => {
    if (!containerNode) {
      return
    }

    setContainerWidth(containerNode.clientWidth)

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width)
      }
    })

    resizeObserver.observe(containerNode)

    return (): void => {
      resizeObserver.disconnect()
    }
  }, [containerNode])

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      dismissable
      overlayClassName="!bg-black/40 z-[54]"
      contentClassName="flex flex-col z-[55] w-[calc(100vw-2rem)] md:w-[min(860px,calc(100vw-2rem))] h-[calc(100dvh-2rem)] md:h-[min(800px,calc(100dvh-2rem))] rounded-lg overflow-hidden bg-surface-muted"
    >
      <DialogTitle asChild>
        <header className="flex items-center justify-between gap-4 bg-surface-page px-6 py-4">
          <div className="flex flex-col gap-1 min-w-0">
            <Typography
              variant={TypographyVariant.titleXs}
              weight={TypographyWeight.light}
              className="text-content-primary truncate"
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant={TypographyVariant.bodyXs}
                className="text-content-secondary truncate"
              >
                {subtitle}
              </Typography>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <a
              href={pdfUrl ?? undefined}
              download={pdfUrl ? `${title}.pdf` : undefined}
              tabIndex={pdfUrl ? 0 : -1}
              aria-disabled={!pdfUrl}
              data-testid="pdf-download-link"
            >
              <Button
                label={undefined}
                className="md:hidden"
                variant={ButtonVariant.primary}
                icon={<AppIcon name={AppIconNames.download} size={16} />}
                size="small"
                disabled={!pdfUrl}
                tabIndex={-1}
                aria-label={t('fundUpdates.pdfPreview.download')}
              />
              <Button
                label={t('fundUpdates.pdfPreview.download')}
                className="hidden md:flex"
                variant={ButtonVariant.primary}
                icon={<AppIcon name={AppIconNames.download} size={16} />}
                size="small"
                disabled={!pdfUrl}
                tabIndex={-1}
              />
            </a>

            <Button
              onClick={onClose}
              variant={ButtonVariant.secondary}
              icon={<AppIcon name={AppIconNames.close} size={16} />}
              aria-label={t('fundUpdates.pdfPreview.close')}
              data-testid="pdf-close-button"
            >
              {t('fundUpdates.pdfPreview.close')}
            </Button>
          </div>
        </header>
      </DialogTitle>

      <div
        ref={setContainerNode}
        className="flex-1 min-h-0 overflow-y-auto bg-surface-muted p-6"
        data-testid="pdf-scroll-container"
      >
        {pdfUrl ? (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            error={
              <div className="flex items-center justify-center h-full">
                <Typography
                  variant={TypographyVariant.bodySm}
                  className="text-content-secondary"
                >
                  {t('fundUpdates.pdfPreview.loadError')}
                </Typography>
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={containerWidth ? containerWidth - 48 : undefined}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="mb-4 last:mb-0 shadow-sm"
              />
            ))}
          </Document>
        ) : (
          <div className="flex flex-col gap-4" aria-hidden>
            {Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                className="w-full rounded animate-skeleton"
                style={{ height: i === 0 ? '420px' : '380px' }}
              />
            ))}
          </div>
        )}
      </div>
    </Dialog>
  )
}
